import React from 'react';
import { Skeleton } from '../ui/Skeleton';
import { ServiceCardSkeleton } from './ServiceCardSkeleton';
import { cn } from '../../utils/cn';
import type { SkeletonAnimation } from '../ui/Skeleton/Skeleton.types';

interface ServicesPageSkeletonProps {
  /**
   * View mode matching the services page
   * @default 'grid'
   */
  viewMode?: 'grid' | 'list';

  /**
   * Number of service cards to display
   * @default 6
   */
  count?: number;

  /**
   * Additional CSS classes
   */
  className?: string;

  /**
   * Animation type
   * @default 'pulse'
   */
  animation?: SkeletonAnimation;
}

/**
 * Skeleton for the services grid/list
 *
 * Used as a loading placeholder while services are being fetched.
 * Matches the ServicesPage layout with grid or list view support.
 */
export const ServicesGridSkeleton: React.FC<ServicesPageSkeletonProps> = ({
  viewMode = 'grid',
  count = 6,
  className,
  animation = 'pulse',
}) => {
  if (viewMode === 'list') {
    return (
      <div className={cn('space-y-4', className)}>
        {Array.from({ length: count }).map((_, index) => (
          <ServiceCardSkeleton key={index} layout="horizontal" animation={animation} />
        ))}
      </div>
    );
  }

  return (
    <div className={cn('grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6', className)}>
      {Array.from({ length: count }).map((_, index) => (
        <ServiceCardSkeleton key={index} layout="vertical" animation={animation} />
      ))}
    </div>
  );
};

/**
 * Full services page skeleton including sidebar filters
 */
export const ServicesPageSkeleton: React.FC<{
  className?: string;
  animation?: SkeletonAnimation;
}> = ({ className, animation = 'pulse' }) => {
  return (
    <div className={cn('min-h-screen bg-gray-50 dark:bg-slate-900', className)}>
      {/* Page Header */}
      <header className="bg-white dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700">
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
          <Skeleton className="h-10 w-80 mb-3" animation={animation} />
          <Skeleton className="h-6 w-full max-w-2xl" animation={animation} />
        </div>
      </header>

      {/* Main Content */}
      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:gap-8">
          {/* Sidebar Filters */}
          <aside className="hidden lg:block lg:w-64 flex-shrink-0">
            <div className="sticky top-24 space-y-6">
              {/* Search */}
              <Skeleton className="h-10 w-full" variant="rectangular" animation={animation} />

              {/* Category Filter */}
              <div>
                <Skeleton className="h-5 w-20 mb-3" animation={animation} />
                <div className="space-y-2">
                  {Array.from({ length: 5 }).map((_, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <Skeleton
                        variant="rectangular"
                        width={16}
                        height={16}
                        animation={animation}
                      />
                      <Skeleton className="h-4 w-24" animation={animation} />
                    </div>
                  ))}
                </div>
              </div>

              {/* Price Range */}
              <div>
                <Skeleton className="h-5 w-24 mb-3" animation={animation} />
                <Skeleton className="h-2 w-full mb-4" variant="rectangular" animation={animation} />
                <div className="flex gap-2">
                  <Skeleton className="h-10 flex-1" variant="rectangular" animation={animation} />
                  <Skeleton className="h-10 flex-1" variant="rectangular" animation={animation} />
                </div>
              </div>

              {/* Duration Filter */}
              <div>
                <Skeleton className="h-5 w-20 mb-3" animation={animation} />
                <div className="space-y-2">
                  {Array.from({ length: 4 }).map((_, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <Skeleton
                        variant="rectangular"
                        width={16}
                        height={16}
                        animation={animation}
                      />
                      <Skeleton className="h-4 w-20" animation={animation} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </aside>

          {/* Main Content Area */}
          <div className="flex-1 min-w-0">
            {/* Toolbar */}
            <div className="mb-6 flex flex-wrap items-center justify-between gap-4 rounded-lg bg-white dark:bg-slate-800 p-4 shadow-sm">
              <Skeleton className="h-5 w-32" animation={animation} />
              <div className="flex items-center gap-3">
                <Skeleton className="h-9 w-28" variant="rectangular" animation={animation} />
                <Skeleton className="h-9 w-20" variant="rectangular" animation={animation} />
              </div>
            </div>

            {/* Services Grid */}
            <ServicesGridSkeleton count={6} animation={animation} />
          </div>
        </div>
      </main>
    </div>
  );
};

ServicesGridSkeleton.displayName = 'ServicesGridSkeleton';
ServicesPageSkeleton.displayName = 'ServicesPageSkeleton';
